import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getFontCss,
  Popup,
  styleConstants,
  TextDropdown,
  themeConstants,
  TripleCheckboxStates,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import { createLocation } from 'history'
import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { getUserConnections } from 'src/app/PeopleContacts/redux/peopleContacts.actions'
import { selectErrorDetailMapFromState } from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import { RegisterOrganizationFields } from 'src/app/RegisterOrganization/RegisterOrganization'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { ThankYouForConfirmingYourOrgToastMessage } from 'src/app/Toast/Toast'
import AppName from 'src/components/AppName/AppName'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgOrganizationInvite from 'src/components/Svgs/SvgOrganizationInvite'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { AvatarSizes } from 'src/enums'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import {
  useIsUserOrglessGuestCardUser,
  useIsUserOrglessRCNUser,
} from 'src/hooks/useOrglessUserStatus'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import { useCreateOrganization } from 'src/queries/organizations/organizationCreate.queries'
import { industryOptionsFromConfig } from 'src/queries/session/config/config.options'
import { sessionConfigQueries } from 'src/queries/session/config/config.queries'
import { createSetIsSetupOrganizationPopupOpen } from 'src/redux/actions/app.actions'
import { createSetPerSessionValues } from 'src/redux/actions/auth.actions'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import { createAbandonPostOrganizationAction } from 'src/redux/actions/data/organizations.actions'
import { getRecentUsers } from 'src/redux/actions/data/users.actions'
import { LocationState } from 'src/redux/createRootReducer'
import { selectConfirmOrgContinueLocation } from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFilteredContactsListModelsFromState } from 'src/redux/selectors/data/contacts.selectors'
import { selectMostRecentlyCreatedCreditCardListModelFromState } from 'src/redux/selectors/data/creditCards.selectors'
import {
  selectMostRecentlyCreatedCreditCardOrganizationListModelFromState,
  selectOrganizationTopLevelErrorFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectUserEmailMap } from 'src/redux/selectors/data/users.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import {
  stringIsEmpty,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

const MAX_PEOPLE_DISPLAYED = 50

const SetupOrganization: React.FC = (): React.ReactNode => {
  const translate = useTranslate()

  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const mostRecentCreditCard = useSelector(
    selectMostRecentlyCreatedCreditCardListModelFromState,
  )
  const mostRecentCreditCardOrganization = useSelector(
    selectMostRecentlyCreatedCreditCardOrganizationListModelFromState,
  )
  const organizationTopLevelError = useSelector(
    selectOrganizationTopLevelErrorFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const currentUser = useCurrentUser()
  const userEmailMap = useSelector(selectUserEmailMap)
  const contacts = useSelector(selectFilteredContactsListModelsFromState)
  const isUserOrglessRCNUser = useIsUserOrglessRCNUser()
  const { analytics } = useAnalyticsContext()
  const location = useSelector(selectLocationFromState)
  const continueLocation = useSelector(selectConfirmOrgContinueLocation)
  const userIsOrglessGuestCardOnlyUser = useIsUserOrglessGuestCardUser()
  // Default [] preserves the legacy redux initial-state render (empty option
  // list while pending) — the config reducer had no loading/error flags.
  const { data: industryOptions = [] } = useQuery({
    ...sessionConfigQueries.byLocale(locale),
    select: industryOptionsFromConfig,
  })

  const dispatch = useDispatch()
  const { mutate: createOrganization } = useCreateOrganization()

  const [selectedTeamMembers, setSelectedTeamMembers] = useState<Set<string>>(
    new Set(),
  )
  const [possibleTeamMembers, setPossibleTeamMembers] = useState<
    UserListModel[]
  >([])

  useEffect(() => {
    dispatch(
      getCreditCards({
        count: 10,
        page: 0,
        type: CreditCardType.Source,
        sortField: 'activeUpdatedAt',
      }),
    )
  }, [])

  useEffect(() => {
    const people = Object.values(userEmailMap).concat(
      contacts.map((contact) => contact?.userTo),
    )
    if (
      people.length > possibleTeamMembers.length &&
      !userIsOrglessGuestCardOnlyUser
    ) {
      // display up to 50 people, exclude current user
      const displayedPeople = people.filter(
        (person) => person.id !== currentUser?.id,
      )
      const filteredDisplayedPeople = displayedPeople.filter(
        (person, i) =>
          i ===
          displayedPeople.findIndex(
            (obj) => JSON.stringify(obj) === JSON.stringify(person),
          ),
      )
      if (filteredDisplayedPeople.length > MAX_PEOPLE_DISPLAYED) {
        filteredDisplayedPeople.length = MAX_PEOPLE_DISPLAYED
      }
      setPossibleTeamMembers(filteredDisplayedPeople)

      // initially select all people
      setSelectedTeamMembers(
        new Set(filteredDisplayedPeople.map((p) => p.email)),
      )
    }
  }, [userEmailMap])

  const handleClose = (): void => {
    dispatch(createAbandonPostOrganizationAction())
    dispatch(createSetIsSetupOrganizationPopupOpen(false))
    if (location.pathname.includes('accounts/budgets')) {
      dispatch(
        createSetPerSessionValues({
          isConfirmBudgetModalAccountsBudgetsDismissed: true,
        }),
      )
    } else if (location.pathname.includes('/overview')) {
      dispatch(
        createSetPerSessionValues({
          isConfirmRCNOrBudgetModalAccountOverviewDismissed: true,
        }),
      )
    } else if (location.pathname.includes('budgets/create')) {
      dispatch(
        createSetPerSessionValues({
          isConfirmBudgetModalCreateBudgetDismissed: true,
        }),
      )
    } else if (location.pathname.includes('/update')) {
      dispatch(
        createSetPerSessionValues({
          isConfirmBudgetModalUpdateBudgetDismissed: true,
        }),
      )
    }

    analytics?.track('orgs for all pop-up - dismiss - button press', {
      intent: 'dismiss',
      interaction: 'button press',
      screen: location.pathname,
      guestOnly: !!userIsOrglessGuestCardOnlyUser,
    })
  }

  const registerOrganizationFieldsValidation =
    (): FormFieldsValidation<RegisterOrganizationFields> => ({
      name: [stringIsEmpty(), stringLengthIsLessThan(45)],
      industry: [stringIsEmpty()],
    })
  const {
    fields: organizationFlatFields,
    fieldsErrors: organizationFlatFieldsErrors,
    onFieldChange: organizationFlatFieldChange,
    setErrorsAndReturnDidPassValidation: organizationFlatFieldsSetErrors,
  } = useFormFields<RegisterOrganizationFields>(
    {
      name: mostRecentCreditCard?.companyName,
      industry: mostRecentCreditCardOrganization?.industry,
    },
    registerOrganizationFieldsValidation,
    errorDetailMap,
    undefined,
  )

  const onRegisterOrganizationSubmit = (
    e: React.FormEvent<HTMLFormElement>,
  ): void => {
    e.preventDefault()
    const organizationFlatFieldsPassedValidation =
      organizationFlatFieldsSetErrors()
    if (organizationFlatFieldsPassedValidation) {
      createOrganization({
        body: organizationFlatFields,
        invitedMembers: Array.from(selectedTeamMembers),
        customCloseModalOnSuccess: () => {
          dispatch(createSetIsSetupOrganizationPopupOpen(false))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <ThankYouForConfirmingYourOrgToastMessage />,
            }),
          )
        },
      })
      if (continueLocation !== '' && continueLocation !== undefined) {
        const actionLocation = createLocation<LocationState>(continueLocation)
        dispatch(connectedPush(actionLocation))
      }

      analytics?.track(
        'orgs for all pop-up - confirm and invite - button press',
        {
          intent: 'confirm and invite',
          interaction: 'button press',
          screen: location.pathname,
          peopleInvited: selectedTeamMembers.size,
          peopleNotInvited:
            possibleTeamMembers.length - selectedTeamMembers.size,
          guestOnly: !!userIsOrglessGuestCardOnlyUser,
        },
      )
    }
  }

  useEffect((): void => {
    dispatch(
      getRecentUsers(
        UserAssociation.delegateCreditCardTo,
        MAX_PEOPLE_DISPLAYED,
      ),
    )
    dispatch(
      getRecentUsers(UserAssociation.virtualCardTo, MAX_PEOPLE_DISPLAYED),
    )
    dispatch(
      getUserConnections({
        page: 0,
        perPage: 50,
      }),
    )
  }, [])

  const handleUserClick = (email: string): void => {
    const newSet = new Set(selectedTeamMembers)
    if (newSet.has(email)) {
      newSet.delete(email)
    } else {
      newSet.add(email)
    }
    setSelectedTeamMembers(newSet)
  }

  return (
    <Popup
      css={css`
        display: flex;
        flex-direction: column;
        z-index: 1;
      `}
      onClickClose={handleClose}
      showCloseButton
    >
      <div
        css={css`
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          ${getFontCss(
            40,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            padding-right: 80px;
          `)}
        `}
      >
        {translate('Confirm your Organization')}
      </div>
      <SvgOrganizationInvite
        css={css`
          position: absolute;
          right: 0;
          top: 0;
          transform: scale(0.75);
          transform-origin: top right;
          border-radius: 10px;
        `}
      />
      <div
        css={css`
          margin-top: 32px;
          display: inline;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {translate(
          'To continue using this feature, ${bolded}. If you believe you should be part of a different org, please contact your card program administrator.',
          {
            bolded:
              possibleTeamMembers.length > 0 ? (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {translate('confirm your org name and invite your team')}
                </span>
              ) : (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {translate('confirm your org name')}
                </span>
              ),
          },
        )}
      </div>
      <form
        onSubmit={onRegisterOrganizationSubmit}
        css={css`
          display: flex;
          flex-direction: column;
        `}
      >
        <Fragment>
          {isUserOrglessRCNUser && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-top: 40px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    20,
                    styleConstants.colors.black,
                    styleConstants.fontFamilies.medium,
                  )}
                `}
              >
                {translate('Organization Info')}
              </span>
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                  display: inline;
                  margin-top: 8px;
                `}
              >
                {translate(
                  "Based on your registered accounts, we've filled out your Org's legal name.",
                )}
              </span>
            </div>
          )}
          <TextInputField
            errors={
              organizationTopLevelError !== undefined
                ? [organizationTopLevelError]
                : organizationFlatFieldsErrors['name']
            }
            design={TextInputFieldDesigns.New}
            fieldLabel={translate("Organization's Legal Name")}
            value={organizationFlatFields['name'] || ''}
            onChange={(val: string): void => {
              organizationFlatFieldChange('name', val)
            }}
            placeholder={translateSingleString(locale, 'Enter your org name')}
            css={css`
              margin-top: 30px;
            `}
          />
          {organizationFlatFields['industry'] === undefined && (
            <TextDropdown
              fieldLabel={translate('Industry')}
              optionGroups={[
                {
                  label: <>{translate('Existing Options')}</>,
                  options: industryOptions,
                },
              ]}
              isMulti={false}
              selectedValues={
                organizationFlatFields['industry'] !== undefined
                  ? new Set([organizationFlatFields['industry']])
                  : new Set<string>()
              }
              chooseOption={(val: string): void => {
                organizationFlatFieldChange('industry', val)
              }}
              placeholder={translateSingleString(
                locale,
                'Select or type in your industry',
              )}
              errors={organizationFlatFieldsErrors['industry']}
              validateNewOption={(val: string): boolean => val.length > 0}
              newOptionLabel={<>{translate('New Label')}</>}
              isCreatable
              css={css`
                margin-top: 20px;
              `}
              inputAriaLabel="Industry"
            />
          )}
          {possibleTeamMembers.length > 0 && (
            <>
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  justify-content: space-between;
                  margin-top: 40px;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      20,
                      styleConstants.colors.black,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  {translate('Team Members')}
                </span>
              </div>
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                  display: inline;
                  margin-top: 8px;
                  margin-bottom: 24px;
                `}
              >
                {translate(
                  'Invite employees at your company in order to get the most out of ${appName}. Based on your activity, you may want to invite these people. Once they become team members, they can receive virtual cards and see who else is in the organization.',
                  {
                    appName: <AppName />,
                  },
                )}
              </span>
              <div
                css={css`
                  background-color: ${styleConstants.colors.gray_lightest};
                  border: 1px solid ${styleConstants.colors.gray_medium};
                  border-radius: 10px;
                `}
              >
                <div
                  css={css`
                    width: 100%;
                    height: 50px;
                    padding: 20px;
                    margin-bottom: 12px;
                    display: flex;
                    box-shadow: 0px 4px 11px rgba(0, 0, 0, 0.05);
                    border-radius: 10px 10px 0px 0px;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                  `}
                >
                  <Checkbox
                    label={translate('Select All')}
                    isSelected={
                      selectedTeamMembers.size === 0
                        ? TripleCheckboxStates.Empty
                        : selectedTeamMembers.size ===
                            possibleTeamMembers.length
                          ? TripleCheckboxStates.Full
                          : TripleCheckboxStates.Partial
                    }
                    onClick={(): void => {
                      const numSelectedBefore = selectedTeamMembers.size
                      let numSelectedAfter = 0
                      if (
                        selectedTeamMembers.size === possibleTeamMembers.length
                      ) {
                        setSelectedTeamMembers(new Set([]))
                      } else {
                        const allPeople = new Set(
                          possibleTeamMembers.map((p) => p.email),
                        )
                        setSelectedTeamMembers(allPeople)
                        numSelectedAfter = allPeople.size
                      }
                      analytics?.track(
                        'confirm org - team members select all - button press',
                        {
                          intent: 'select all team members',
                          interaction: 'button press',
                          screen: location.pathname,
                          numSelectedBeforePress: numSelectedBefore,
                          numSelectedAfterPress: numSelectedAfter,
                        },
                      )
                    }}
                  />
                  <span
                    css={css`
                      ${getFontCss(
                        14,
                        styleConstants.colors.gray_darkest,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    {translate(
                      selectedTeamMembers.size === 1
                        ? '${number} Team Member selected'
                        : '${number} Team Members selected',
                      {
                        number: selectedTeamMembers.size.toString(),
                      },
                    )}
                  </span>
                </div>
                <div
                  css={css`
                    border-radius: 0 0 10px 10px;
                    overflow: auto;
                    max-height: 246px;
                  `}
                >
                  {Object.values(possibleTeamMembers).map((person, i) => (
                    <Checkbox
                      key={i}
                      isSelected={selectedTeamMembers.has(person.email)}
                      onClick={(): void => handleUserClick(person.email)}
                      css={css`
                        height: auto;
                        padding: 13px 20px 12px;
                        ${i === Object.values(possibleTeamMembers).length - 1 &&
                        css`
                          border-radius: 0 0 10px 10px;
                        `}
                        &:hover {
                          background-color: ${styleConstants.colors.gray_light};
                        }
                      `}
                      label={
                        <div
                          css={css`
                            display: flex;
                            flex-direction: row;
                            align-items: center;
                            margin-left: 6px;
                          `}
                        >
                          <UserAvatar
                            css={css`
                              margin-right: 10px;
                            `}
                            user={person}
                            size={AvatarSizes.Small}
                          />
                          <div
                            css={css`
                              display: flex;
                              flex-direction: column;
                            `}
                          >
                            {person.firstName !== undefined &&
                              person.lastName !== undefined && (
                                <span
                                  css={css`
                                    ${getFontCss(
                                      16,
                                      themeConstants.colors.font_primary,
                                      styleConstants.fontFamilies.medium,
                                    )};
                                  `}
                                >
                                  {`${person.firstName} ${person.lastName} `}
                                </span>
                              )}
                            <span
                              css={css`
                                ${getFontCss(
                                  12,
                                  styleConstants.colors.gray_darkest,
                                  styleConstants.fontFamilies.regular,
                                )};
                              `}
                            >
                              {person.email}
                            </span>
                          </div>
                        </div>
                      }
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </Fragment>
        <div
          css={css`
            display: flex;
            align-items: center;
            margin-top: 40px;
          `}
        >
          <Button
            type={ButtonTypes.submit}
            design={ButtonDesigns.designCta}
            big
            text={
              <>
                {possibleTeamMembers.length > 0
                  ? translate('Confirm and Invite (${number})', {
                      number: selectedTeamMembers.size.toString(),
                    })
                  : translate('Confirm')}
              </>
            }
          />
          <HoverableLink
            ariaLabelSource={'Cancel'}
            onClick={handleClose}
            css={css`
              margin-left: 20px;
            `}
          >
            {translate('Cancel')}
          </HoverableLink>
        </div>
      </form>
    </Popup>
  )
}

export default SetupOrganization
